import { host } from './API';
import { encryptPassword } from '../Helpers/PasswordEncryption';

export const login = async function (email: string, password: string) {
    const endpoint = host + "api/auth/login";

    // Encrypt password before sending
    const encryptedPassword = await encryptPassword(password);

    const response = await fetch(
        endpoint,
        {
            method: 'POST',
            headers: {
                'Content-type': 'application/json'
            },
            body: JSON.stringify({
                'email': email,
                'password': encryptedPassword
            })
        });

    if (!response.ok) {
        const message = 'Failed to log in. Please check your email and password and try again.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;
}

export const signUp = async function (name: string,
    businessName: string | undefined,
    email: string, password: string,
    plan: string,
    subscriptionId: string | null,
    token: string | null) {

    const endpoint = host + "api/auth/signup";

    // Encrypt password before sending
    const encryptedPassword = await encryptPassword(password);

    const response = await fetch(
        endpoint,
        {
            method: 'POST',
            headers: {
                'Content-type': 'application/json'
            },
            body: JSON.stringify({
                'email': email,
                'password': encryptedPassword,
                'name': name,
                'businessName': businessName,
                'subscription_id': subscriptionId,
                'token': token
            })
        });

    if (!response.ok) {
        const message = 'Failed to sign up. Please verify your information and try again.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;
}

export const reset = async function (token: string, newPassword: string) {
    const endpoint = host + "api/auth/reset-password";

    // Encrypt new password before sending
    const encryptedNewPassword = await encryptPassword(newPassword);

    const response = await fetch(
        endpoint,
        {
            method: 'POST',
            headers: {
                'Content-type': 'application/json'
            },
            body: JSON.stringify({
                'token': token,
                'newPassword': encryptedNewPassword
            })
        });

    if (!response.ok) {
        const message = 'Failed to reset password. Please try again or contact support.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;
}

export const sendRecoveryEmail = async function (email: string) {
    const endpoint = host + "api/auth/recover";

    const response = await fetch(
        endpoint,
        {
            method: 'POST',
            headers: {
                'Content-type': 'application/json'
            },
            body: JSON.stringify({
                'email': email
            })
        });

    if (!response.ok) {
        const message = 'Failed to send recovery email. Please check the email address and try again.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;
}

export const getUser = async function (token: string) {
    const endpoint = host + "api/auth/user";

    const response = await fetch(
        endpoint,
        {
            method: 'GET',
            headers: {
                'Content-type': 'application/json',
                'Authorization': 'Bearer ' + token
            }
        });

    if (!response.ok) {
        const message = 'Failed to retrieve user information. Please try again or contact support.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;
}

export const getTeam = async function (token: string) {
    const endpoint = host + "api/auth/team";

    const response = await fetch(
        endpoint,
        {
            method: 'GET',
            headers: {
                'Content-type': 'application/json',
                'Authorization': 'Bearer ' + token
            }
        });

    if (!response.ok) {
        const message = 'Failed to retrieve team information. Please try again or contact support.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;
}

export const sendInvite = async function (token: string, email: string, subscriptionId: string, role: string) {
    const endpoint = host + "api/auth/invite";

    const response = await fetch(
        endpoint,
        {
            method: 'POST',
            headers: {
                'Content-type': 'application/json',
                'Authorization': 'Bearer ' + token
            },
            body: JSON.stringify({
                'email': email,
                'subscriptionId': subscriptionId,
                'role': role
            })
        });

    if (!response.ok) {
        const message = 'Failed to send invite. Please check the information and try again.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;
}

export const getInvites = async function (token: string) {
    const endpoint = host + "api/auth/invites";

    const response = await fetch(
        endpoint,
        {
            method: 'GET',
            headers: {
                'Content-type': 'application/json',
                'Authorization': 'Bearer ' + token
            }
        });

    if (!response.ok) {
        const message = 'Failed to retrieve invites. Please try again or contact support.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;
}

export const removeUser = async function (token: string, userId: string, subscriptionId: string) {
    const endpoint = host + 'api/auth/subscription/' + subscriptionId + '/remove?userId=' + userId;

    const response = await fetch(
        endpoint,
        {
            method: 'DELETE',
            headers: {
                'Content-type': 'application/json',
                'Authorization': 'Bearer ' + token
            }
        });

    if (!response.ok) {
        const message = 'Failed to remove user. Please try again or contact support.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;

}

export const getKeysForSubscription = async function (token: string, subscriptionId: string) {
    const endpoint = host + 'api/auth/subscription/' + subscriptionId + '/config';

    const response = await fetch(
        endpoint,
        {
            method: 'GET',
            headers: {
                'Content-type': 'application/json',
                'Authorization': 'Bearer ' + token
            }
        });

    if (!response.ok) {
        const message = 'Failed to retrieve keys. Please try again or contact support.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;

}

export const updateKeysForSubscription = async function (token: string, subscriptionId: string) {
    const endpoint = host + 'api/auth/subscription/' + subscriptionId + '/config';

    const response = await fetch(
        endpoint,
        {
            method: 'POST',
            headers: {
                'Content-type': 'application/json',
                'Authorization': 'Bearer ' + token
            }
        });

    if (!response.ok) {
        const message = 'Failed to update keys. Please try again or contact support.';
        throw new Error(message);
    }

    const results = await response.json();

    return results;
}